/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/* istanbul ignore file */
// NOTE!
// This file is being ignored in the test coverage report for now. It reports `0%` functions
// tested, which brings down the overall coverage and blocks CI. There are tests still, but
// we don't want it to count toward coverage until we figure out how to cover the `functions`
// metric for this file in its test.

import React from 'react'

import loadable from '@loadable/component'
import {Redirect} from 'react-router-dom'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'

// Components
import {Skeleton} from '@chakra-ui/react'
import {configureRoutes} from './utils/routes-utils'
import useMultiSite from './hooks/use-multi-site'
import {
    HOME_HREF,
    PASSWORDLESS_LOGIN_LANDING_PATH,
    PDP_ROUTE_PATH,
    PLP_ROUTE_PATH,
    PWA_RESET_PASSWORD_ENABLED,
    STORE_LOCATOR_IS_ENABLED,
    RESET_PASSWORD_LANDING_PATH,
    WARRANTY_REGISTER_HREF,
    ORDERSTATUS_HREF,
    CPSIA_HREF
} from './constants'
import {resolveSiteFromUrl} from './utils/site-utils'
import {getCorrespondingHomePage} from './utils/url'
import {isServer, resolveLocaleFromUrl} from './utils/utils'
import CustomerServiceSkeleton from './pages/customer-service/skeleton'

const fallback = <Skeleton height="75vh" width="100%" />
const socialRedirectURI = getConfig()?.app?.login?.social?.redirectURI

// Pages
const Home = loadable(() => import('./pages/home'), {fallback})
const Login = loadable(() => import('./pages/login'), {fallback})
const Registration = loadable(() => import('./pages/registration'), {fallback})
const ResetPassword = loadable(() => import('./pages/reset-password'), {fallback})
const CreateNewPassword = loadable(() => import('./pages/create-new-password'), {fallback})
const Cart = loadable(() => import('./pages/cart'), {fallback})
const Checkout = loadable(() => import('./pages/checkout'), {fallback})
const CheckoutConfirmation = loadable(() => import('./pages/checkout/confirmation'), {fallback})
const CustomerService = loadable(() => import('./pages/customer-service'), {
    fallback: CustomerServiceSkeleton
})
const LeapContent = loadable(() => import('./pages/leap-content'), {fallback})
const LoginRedirect = loadable(() => import('./pages/login-redirect'), {fallback})
const MyStokke = loadable(() => import('./pages/my-stokke'), {fallback})
const ProductDetail = loadable(() => import('./pages/product-detail'), {fallback})
const ProductList = loadable(() => import('./pages/product-list'), {fallback})
const CountrySelector = loadable(() => import('./pages/country-selector'))
const PageNotFound = loadable(() => import('./pages/page-not-found'))
const Content = loadable(() => import('./pages/content'), {fallback})
const CheckoutCancellation = loadable(() => import('./pages/checkout/cancellation'), {fallback})
const ShipmentTracking = loadable(() => import('./pages/shipment-tracking'), {fallback})
const ReturnOrder = loadable(() => import('./pages/return-order'), {fallback})
const Withdrawal = loadable(() => import('./pages/withdrawal'), {fallback})
const SocialLoginRedirect = loadable(() => import('./pages/social-login-redirect'), {fallback})
const Momentvm = loadable(() => import('./pages/momentvm'), {fallback})
const StoreLocator = loadable(() => import('./pages/store-locator'), {
    fallback
})
const WarrantyPortal = loadable(() => import('./pages/warranty-portal'), {fallback})
const ExplorationTest = loadable(() => import('./pages/exploration-test'), {fallback})
const UrlMappedRouting = loadable(() => import('./pages/dynamic-routing/url-mapped-routing'), {
    fallback
})

const RedirectToHome = () => {
    // STOK-14304 this functionality won't be executed due to missing accpet-language header
    // instead of that - the SFCC controller would redirect to corresponding home page
    // code would be kept for further investigation
    const {req, res} = useServerContext()
    let correspondingHomePage
    if (req?.path === '/') {
        const language = req.headers?.['accept-language']?.split(';')[0]?.split(',')[0]
        const countryCode = language?.split('-')?.[1]
        correspondingHomePage = getCorrespondingHomePage(countryCode)
        res.set('vary', ['accept-encoding', 'accept-language'].join(', '))
    }
    const {buildUrl} = useMultiSite()
    return <Redirect to={correspondingHomePage || buildUrl(HOME_HREF)} />
}

const ExplorationTestWrapper = (Page) => {
    const page = () => {
        const {app} = getConfig()
        if (!app.explorationTestEnabled) {
            return <RedirectToHome />
        }

        return <Page />
    }
    if (process.env.NODE_ENV === 'development') {
        page.displayName = `ExplorationTestWrapper(${Page.displayName || Page.name})`
    }
    return page
}

const routes = [
    {
        path: '/',
        pageType: 'root',
        component: RedirectToHome,
        exact: true
    },
    {
        path: '/home',
        pageType: 'home',
        component: Home,
        exact: true
    },
    {
        path: '/login',
        pageType: 'login',
        component: Login,
        exact: true
    },
    {
        path: '/registration',
        pageType: 'registration',
        component: Registration,
        exact: true
    },
    {
        path: '/reset-password',
        pageType: 'reset-password',
        component: ResetPassword,
        exact: true
    },
    {
        path: '/exploration/test',
        pageType: 'exploration-test',
        component: ExplorationTestWrapper(ExplorationTest),
        exact: true
    },
    ...(PWA_RESET_PASSWORD_ENABLED
        ? [
              {
                  path: RESET_PASSWORD_LANDING_PATH,
                  pageType: 'reset-password',
                  component: ResetPassword,
                  exact: true
              },
              {
                  path: PASSWORDLESS_LOGIN_LANDING_PATH,
                  pageType: 'login',
                  component: Login,
                  exact: true
              }
          ]
        : []),
    // maybe these routes are not needed when PWA_RESET_PASSWORD_ENABLED is true
    {
        path: '/create-new-password',
        pageType: 'create-password',
        component: CreateNewPassword,
        exact: true
    },
    {
        path: '/set-password',
        pageType: 'create-password',
        component: CreateNewPassword,
        exact: true
    },
    // end of maybe not needed
    {
        path: '/my-stokke',
        pageType: 'account',
        component: MyStokke
    },
    // TO BE REMOVED
    {
        path: '/support/:pageId*',
        pageType: 'customer-service',
        component: CustomerService
    },
    {
        path: '/customer-service/:pageId*',
        pageType: 'customer-service',
        component: CustomerService
    },
    {
        path: '/contact-us',
        pageType: 'customer-service',
        component: () => <CustomerService pageId="contact-form" pageType="content" />
    },
    {
        path: CPSIA_HREF,
        pageType: 'warranty',
        component: () => <WarrantyPortal mode="cpsia" />
    },
    {
        path: WARRANTY_REGISTER_HREF,
        pageType: 'warranty',
        component: () => <WarrantyPortal mode="warranty" />
    },
    {
        path: '/appcheckout',
        pageType: 'checkout',
        component: Checkout,
        exact: true
    },
    {
        path: '/appcheckout/confirmation',
        pageType: 'checkout-confirmation',
        component: CheckoutConfirmation,
        exact: true
    },
    {
        path: '/callback',
        pageType: 'login-callback',
        component: LoginRedirect,
        exact: true
    },
    {
        path: socialRedirectURI || '/social-callback',
        pageType: 'social-login-callback',
        component: SocialLoginRedirect,
        exact: true
    },
    {
        path: '/appcart',
        pageType: 'cart',
        component: Cart,
        exact: true
    },
    {
        path: '/ct/:contentId.leap.html',
        pageType: 'leap-content',
        component: ExplorationTestWrapper(LeapContent)
    },
    {
        path: '/(stokke|ct|newsroom)/:contentId.html',
        pageType: 'content',
        component: Content
    },
    {
        path: '/(stokke|ct|newsroom)/*/:contentId.html',
        pageType: 'content',
        component: Content
    },
    {
        // legacy PWA content urls
        path: '/content/:contentId',
        pageType: 'content',
        component: Content
    },
    {
        path: '/search',
        pageType: 'search',
        component: ProductList
    },
    {
        path: '/category/:categoryPath+',
        pageType: 'url-mapped',
        component: UrlMappedRouting
    },
    {
        path: '/country-selector',
        pageType: 'country-selector',
        component: CountrySelector
    },
    {
        path: '/cancel-order/:hash',
        pageType: 'checkout-cancellation',
        component: CheckoutCancellation
    },
    {
        path: PDP_ROUTE_PATH,
        pageType: 'pdp',
        component: ProductDetail
    },
    // legacy product URLs
    {
        path: '/product/:productId',
        pageType: 'pdp',
        component: ProductDetail
    },
    {
        path: '/shipment-tracking',
        pageType: 'shipment-tracking',
        component: ShipmentTracking
    },
    {
        path: ORDERSTATUS_HREF,
        pageType: 'shipment-tracking',
        component: ShipmentTracking
    },
    {
        path: '/momentvm',
        pageType: 'momentvm',
        component: Momentvm
    },
    {
        path: '/return-order',
        pageType: 'return-order',
        component: ReturnOrder
    },
    {
        path: '/withdrawal',
        pageType: 'withdrawal',
        component: Withdrawal,
        exact: true
    },
    ...(STORE_LOCATOR_IS_ENABLED
        ? [
              {
                  path: '/stores',
                  pageType: 'store-locator',
                  component: StoreLocator
              }
          ]
        : []),
    // Dynamic routing - keep this as the second-to-last route
    {
        path: PLP_ROUTE_PATH,
        pageType: 'url-mapped',
        component: UrlMappedRouting
    },
    // Default route if none of the above matched - keep this as the last route
    {
        path: '*',
        pageType: 'not-found',
        component: PageNotFound
    }
]

export default () => {
    const config = getConfig()
    const currentPath = isServer ? null : `${window.location.pathname}${window.location.search}`
    const options = {
        ignoredRoutes: ['/callback', '*'],
        ...(currentPath
            ? // filter generated routes to the current site and locale on the client
              // change of site or locale requires reload, so we don't need
              // to handle all other sites and locales in the client
              {
                  site: resolveSiteFromUrl(currentPath),
                  locale: resolveLocaleFromUrl(currentPath)
              }
            : {})
    }

    return configureRoutes(routes, config, options)
}
